import React, { useEffect, useMemo } from 'react';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Card } from '../../../CommonComponents/OPL/Card/Card';
import { Loading } from '../../../CommonComponents/OPL/Loading/Loading';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../CommonUtils/formatNumber';
import { useReconciliationProposalQuery } from '../api/useReconciliationProposalQuery';
import { CategoryAccordion } from '../components/CategoryAccordion';
import { CATEGORIES, allDecisionsMade, seedDecisions } from '../meta';
import { CategorySection, Decisions, ReconciliationStatusResult } from '../pdfReconciliationTypes';

interface Props {
  batchId: string;
  itemId: string;
  decisions: Decisions;
  onDecisionsChange: (next: Decisions) => void;
  onProposalLoaded: (data: ReconciliationStatusResult) => void;
  onContinueReady: (ready: boolean) => void;
}

const HouseholdSummaryHeader = ({ data }: { data: ReconciliationStatusResult }) => {
  return (
    <Box
      display="grid"
      mb="24"
      css={({ theme }) => ({
        gap: theme.space[12],
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      })}
    >
      {data.principalName && (
        <Card p="16" hasBorder>
          <Text variant="smallCaps" fontSize="h6" color="neutral.500" mb="4">
            Principal
          </Text>
          <Text fontWeight={600} fontSize="h5">
            {data.principalName}
          </Text>
          {data.principalAge != null && (
            <Text fontSize="small" color="neutral.500" mt="2">
              Age {data.principalAge}
            </Text>
          )}
        </Card>
      )}
      {data.spouseName && (
        <Card p="16" hasBorder>
          <Text variant="smallCaps" fontSize="h6" color="neutral.500" mb="4">
            Spouse
          </Text>
          <Text fontWeight={600} fontSize="h5">
            {data.spouseName}
          </Text>
          {data.spouseAge != null && (
            <Text fontSize="small" color="neutral.500" mt="2">
              Age {data.spouseAge}
            </Text>
          )}
        </Card>
      )}
      {data.netWorth != null && (
        <Card p="16" hasBorder>
          <Text variant="smallCaps" fontSize="h6" color="neutral.500" mb="4">
            Net Worth
          </Text>
          <Text fontWeight={600} fontSize="h5">
            {formatCurrency(data.netWorth)}
          </Text>
        </Card>
      )}
    </Box>
  );
};

export const ReviewDifferencesStep = ({
  batchId,
  itemId,
  decisions,
  onDecisionsChange,
  onProposalLoaded,
  onContinueReady,
}: Props) => {
  const { data, isLoading, error } = useReconciliationProposalQuery({
    batchId,
    itemId,
    enabled: true,
  });

  // When the proposal first arrives, seed default decisions and notify the parent.
  useEffect(() => {
    if (!data?.proposal) {
      return;
    }

    onProposalLoaded(data);

    if (Object.keys(decisions).length === 0) {
      onDecisionsChange(seedDecisions(data.proposal));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status]);

  const ready = useMemo(() => {
    if (!data?.proposal) {
      return false;
    }

    return allDecisionsMade(data.proposal, decisions);
  }, [data?.proposal, decisions]);

  useEffect(() => {
    onContinueReady(ready);
  }, [ready, onContinueReady]);

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py="48">
        <Loading />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        variant="error"
        title="Failed to load reconciliation"
        message="An error occurred while loading the import."
      />
    );
  }

  if (!data) {
    return null;
  }

  if (data.status === 'Failed') {
    return (
      <Alert
        variant="error"
        title="Import failed"
        message={(data.errors && data.errors.join(' ')) || 'The PDF could not be processed.'}
      />
    );
  }

  if (data.status === 'Analyzing' || data.status === 'Pending') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        py="48"
        css={({ theme }) => ({ gap: theme.space[12] })}
      >
        <Loading text="Analyzing the PDF and matching against this household…" />
      </Box>
    );
  }

  if (!data.proposal) {
    return null;
  }

  const proposal = data.proposal;

  return (
    <Box>
      {proposal.principalMismatchWarning && (
        <Box mb="16">
          <Alert
            variant="warning"
            title="This PDF may belong to a different client"
            message={proposal.principalMismatchWarning}
          />
        </Box>
      )}

      {data.summary && (
        <Box mb="16">
          <Text color="neutral.600">{data.summary}</Text>
        </Box>
      )}

      <HouseholdSummaryHeader data={data} />

      {CATEGORIES.map(def => {
        const section = proposal[def.key] as CategorySection | undefined;

        if (!section) {
          return null;
        }

        return (
          <CategoryAccordion
            key={def.key}
            def={def}
            section={section}
            decisions={decisions}
            onChange={onDecisionsChange}
          />
        );
      })}
    </Box>
  );
};
